<template>
  <div class="seo-pillar-page">
    <SiteHeader/>

    <section class="seo-hero">
      <v-container class="pt-16 pb-12 text-center">
        <h1 class="seo-hero-title reveal" style="--reveal-index: 0">{{ content.h1 }}</h1>
        <p class="seo-hero-intro reveal mx-auto" style="--reveal-index: 1">{{ content.intro }}</p>
      </v-container>
    </section>

    <section class="seo-section seo-problem-solution">
      <v-container class="pb-12">
        <v-row>
          <v-col cols="12" md="6" class="reveal" style="--reveal-index: 0">
            <h2 class="section-title">{{ content.problem.title }}</h2>
            <p class="seo-body-text">{{ content.problem.body }}</p>
          </v-col>
          <v-col cols="12" md="6" class="reveal" style="--reveal-index: 1">
            <h2 class="section-title">{{ content.solution.title }}</h2>
            <p class="seo-body-text">{{ content.solution.body }}</p>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="seo-section seo-benefits-section">
      <v-container class="pb-12">
        <h2 class="section-title text-center mx-auto">{{ content.benefitsTitle }}</h2>
        <v-row class="mt-8">
          <v-col cols="12" sm="6" md="4" v-for="(item, index) in content.benefits" :key="`benefit-${index}`" class="reveal" :style="{ '--reveal-index': index % 4 }">
            <div class="seo-card">
              <v-icon size="28" color="#00BDB3" class="mb-2">{{ item.icon }}</v-icon>
              <h3 class="seo-card-title">{{ item.title }}</h3>
              <p class="seo-card-text">{{ item.description }}</p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="seo-section seo-how-it-works">
      <v-container class="pb-12">
        <h2 class="section-title text-center mx-auto">{{ content.howItWorksTitle }}</h2>
        <v-row class="mt-8">
          <v-col cols="12" sm="6" md="3" v-for="(step, index) in content.steps" :key="`step-${index}`" class="reveal" :style="{ '--reveal-index': index }">
            <div class="seo-step">
              <span class="seo-step-number">{{ index + 1 }}</span>
              <h3 class="seo-card-title">{{ step.title }}</h3>
              <p class="seo-card-text">{{ step.description }}</p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="seo-section seo-features-section">
      <v-container class="pb-12">
        <h2 class="section-title text-center mx-auto">{{ content.featuresTitle }}</h2>
        <v-row class="mt-8">
          <v-col cols="12" sm="6" md="4" v-for="(item, index) in content.features" :key="`feature-${index}`" class="reveal" :style="{ '--reveal-index': index % 4 }">
            <div class="seo-card">
              <v-icon size="28" color="#0277BD" class="mb-2">{{ item.icon }}</v-icon>
              <h3 class="seo-card-title">{{ item.title }}</h3>
              <p class="seo-card-text">{{ item.description }}</p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="seo-section seo-use-cases-section">
      <v-container class="pb-12">
        <h2 class="section-title text-center mx-auto">{{ content.useCasesTitle }}</h2>
        <v-row class="mt-8">
          <v-col cols="12" md="6" v-for="(item, index) in content.useCases" :key="`use-case-${index}`" class="reveal" :style="{ '--reveal-index': index % 4 }">
            <div class="seo-card seo-use-case-card">
              <h3 class="seo-card-title">{{ item.title }}</h3>
              <p class="seo-card-text">{{ item.description }}</p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="seo-section seo-cross-pillar" v-if="content.crossPillar">
      <v-container class="pb-4">
        <p class="seo-cross-pillar-text reveal" style="--reveal-index: 0">
          {{ content.crossPillar.text }}
          <a :href="content.crossPillar.href" class="seo-inline-link">{{ content.crossPillar.label }}</a>.
        </p>
      </v-container>
    </section>

    <section class="seo-section seo-faq-section" v-if="faqItems.length">
      <v-container class="pb-12">
        <h2 class="section-title text-center mx-auto">{{ content.faqTitle }}</h2>
        <v-expansion-panels flat class="seo-faq-panels mt-8">
          <v-expansion-panel v-for="(item, index) in faqItems" :key="`faq-${index}`">
            <v-expansion-panel-header class="seo-faq-question">{{ item.question }}</v-expansion-panel-header>
            <v-expansion-panel-content class="seo-faq-answer">{{ item.answer }}</v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-container>
    </section>

    <section class="seo-section seo-internal-links-section" v-if="content.internalLinks && content.internalLinks.length">
      <v-container class="pb-12">
        <h2 class="section-title text-center mx-auto">{{ content.internalLinksTitle }}</h2>
        <div class="seo-internal-links-list mt-6">
          <a v-for="(link, index) in content.internalLinks" :key="`internal-${index}`" :href="link.href" class="seo-internal-link-card">
            {{ link.label }}
            <v-icon size="16" color="#0277BD">mdi-arrow-top-right-thin</v-icon>
          </a>
        </div>
      </v-container>
    </section>

    <section class="seo-cta-section">
      <v-container class="pb-16 text-center">
        <v-btn :href="signupHref" x-large rounded color="#0277BD" class="text-none seo-cta-primary" dark>
          {{ content.ctaPrimaryLabel }}
        </v-btn>
        <a href="/" class="seo-cta-secondary d-block mt-4">{{ content.ctaSecondaryLabel }}</a>
      </v-container>
    </section>

    <SiteFooter/>
  </div>
</template>

<script>
import SiteHeader from "./SiteHeader.vue";
import SiteFooter from "./SiteFooter.vue";

export default {
  name: 'SeoPillarLandingPage',
  props: {
    content: { type: Object, required: true },
    faqSrc: { type: String, required: true },
  },
  components: {
    SiteHeader,
    SiteFooter,
  },
  data: () => ({
    faqItems: [],
    revealObserver: null,
  }),
  mounted: function() {
    fetch(this.faqSrc)
      .then(response => response.json())
      .then(data => {
        this.faqItems = data.items || []
      })
      .catch(() => {
        this.faqItems = []
      })
    this.$nextTick(() => this.setupRevealObserver())
  },
  beforeDestroy: function() {
    if (this.revealObserver) this.revealObserver.disconnect()
  },
  computed: {
    signupHref: function() {
      return `${process.env.VUE_APP_API_V1_URL}/oauth2/authorization/google`
    },
  },
  methods: {
    setupRevealObserver: function() {
      const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const elements = this.$el.querySelectorAll ? this.$el.querySelectorAll('.reveal') : []
      if (prefersReducedMotion || !window.IntersectionObserver) {
        elements.forEach(el => el.classList.add('is-visible'))
        return
      }
      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            this.revealObserver.unobserve(entry.target)
          }
        })
      }, { threshold: 0.15 })
      elements.forEach(el => this.revealObserver.observe(el))
    },
  },
}
</script>
<style scoped>
.seo-pillar-page {
  font-family: 'Montserrat', sans-serif;
  background: #F7F5EF;
  padding-top: 72px;
}
.reveal {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1);
  transition-delay: calc(var(--reveal-index, 0) * 90ms);
}
.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}
@media (prefers-reduced-motion: reduce) {
  .reveal {
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}
.seo-hero {
  background: #F7F5EF;
}
.seo-hero-title {
  font-weight: 900;
  font-size: 2.4rem;
  line-height: 1.2;
  color: #0277BD;
  letter-spacing: -0.01em;
}
.seo-hero-intro {
  color: #5B6472;
  font-size: 1.05rem;
  max-width: 640px;
  margin-top: 1rem;
}
.section-title {
  font-weight: 800;
  font-size: 1.6rem;
  color: #101827;
}
.seo-body-text {
  color: #5B6472;
  font-size: 1rem;
  line-height: 1.6;
  margin-top: 0.75rem;
}
.seo-card {
  background: #FFFFFF;
  border-radius: 1rem;
  padding: 1.5rem;
  height: 100%;
  box-shadow: 0 8px 20px rgba(16,24,39,0.05);
}
.seo-use-case-card {
  border-left: 3px solid #00BDB3;
}
.seo-card-title {
  font-weight: 700;
  font-size: 1rem;
  color: #101827;
  margin: 0 0 0.4rem;
}
.seo-card-text {
  color: #5B6472;
  font-size: 0.9rem;
  line-height: 1.55;
  margin: 0;
}
.seo-step {
  text-align: center;
  padding: 1rem;
}
.seo-step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #0277BD;
  color: #FFFFFF;
  font-weight: 800;
  margin-bottom: 0.75rem;
}
.seo-cross-pillar-text {
  text-align: center;
  color: #5B6472;
  font-size: 1rem;
  max-width: 680px;
  margin: 0 auto;
}
.seo-inline-link {
  color: #0277BD;
  font-weight: 700;
  text-decoration: none;
}
.seo-inline-link:hover {
  text-decoration: underline;
}
.seo-faq-panels {
  background: transparent;
}
.seo-faq-question {
  font-weight: 700;
  color: #101827;
}
.seo-faq-answer {
  color: #5B6472;
  line-height: 1.6;
}
.seo-internal-links-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
}
.seo-internal-link-card {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #FFFFFF;
  border-radius: 999px;
  padding: 0.6rem 1.1rem;
  color: #0277BD;
  font-weight: 700;
  font-size: 0.85rem;
  text-decoration: none;
  box-shadow: 0 6px 16px rgba(16,24,39,0.05);
  transition: transform 0.2s ease;
}
.seo-internal-link-card:hover {
  transform: translateY(-2px);
}
.seo-cta-section {
  background: #FFFFFF;
}
.seo-cta-primary {
  font-weight: 700;
  padding: 0 2.5rem !important;
}
.seo-cta-secondary {
  color: #5B6472;
  font-weight: 600;
  text-decoration: none;
}
.seo-cta-secondary:hover {
  color: #0277BD;
}
</style>
